import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import HotelCard from '../components/HotelCard';
import SearchBar from '../components/SearchBar';
import './Hotels.css';

const TYPES = ['All', 'hotel', 'resort', 'villa', 'boutique', 'apartment', 'hostel'];

const Hotels = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);

  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [filters, setFilters] = useState({
    type: params.get('type') || 'All',
    min: '',
    max: '',
    city: params.get('city') || ''
  });

  const fetchHotels = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams();
      if (filters.city) query.set('city', filters.city);
      if (filters.type && filters.type !== 'All') query.set('type', filters.type);
      if (filters.min) query.set('min', filters.min);
      if (filters.max) query.set('max', filters.max);
      query.set('page', page);
      query.set('limit', '9');

      const res = await axios.get(`/api/hotels?${query.toString()}`);
      setHotels(res.data.hotels);
      setTotal(res.data.total);
      setTotalPages(res.data.pages);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ type: 'All', min: '', max: '', city: '' });
    setPage(1);
    navigate('/hotels');
  };

  return (
    <div className="hotels-page">
      <Navbar />

      {/* Search strip */}
      <div className="hotels-search-strip">
        <div className="container">
          <SearchBar compact />
        </div>
      </div>

      <div className="hotels-layout container">
        {/* Sidebar Filters */}
        <aside className="filters-sidebar">
          <div className="filters-card">
            <div className="filters-header">
              <h3>Filters</h3>
              <button className="clear-btn" onClick={clearFilters}>Clear all</button>
            </div>

            {/* Destination */}
            <div className="filter-section">
              <label className="filter-label">Destination</label>
              <input
                type="text"
                placeholder="City or country..."
                className="form-input"
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
              />
            </div>

            {/* Property Type */}
            <div className="filter-section">
              <label className="filter-label">Property Type</label>
              <div className="type-filters">
                {TYPES.map(t => (
                  <button
                    key={t}
                    className={`type-filter-btn ${filters.type === t ? 'active' : ''}`}
                    onClick={() => handleFilterChange('type', t)}
                  >
                    {t === 'All' ? 'All Types' : t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Range */}
            <div className="filter-section">
              <label className="filter-label">Price per Night (USD)</label>
              <div className="price-inputs">
                <div className="price-input-wrap">
                  <span className="price-symbol">$</span>
                  <input
                    type="number"
                    placeholder="Min"
                    className="form-input price-input"
                    value={filters.min}
                    onChange={(e) => handleFilterChange('min', e.target.value)}
                  />
                </div>
                <span className="price-dash">—</span>
                <div className="price-input-wrap">
                  <span className="price-symbol">$</span>
                  <input
                    type="number"
                    placeholder="Max"
                    className="form-input price-input"
                    value={filters.max}
                    onChange={(e) => handleFilterChange('max', e.target.value)}
                  />
                </div>
              </div>
            </div>

            <button className="btn btn-dark" style={{width:'100%'}} onClick={fetchHotels}>
              Apply Filters
            </button>
          </div>
        </aside>

        {/* Results */}
        <main className="hotels-results">
          <div className="results-header">
            <div>
              <h2 className="results-title">
                {filters.city ? `Hotels in ${filters.city}` : 'All Properties'}
              </h2>
              <p className="results-count">
                {loading ? 'Searching...' : `${total} propert${total !== 1 ? 'ies' : 'y'} found`}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="page-loader"><div className="spinner" /></div>
          ) : hotels.length === 0 ? (
            <div className="no-results">
              <div className="no-results-icon"><i class="fa-solid fa-magnifying-glass"></i></div>
              <h3>No properties found</h3>
              <p>Try adjusting your filters or search for a different destination</p>
              <button className="btn btn-dark" onClick={clearFilters}>Clear Filters</button>
            </div>
          ) : (
            <>
              <div className="results-grid">
                {hotels.map(hotel => (
                  <HotelCard key={hotel._id} hotel={hotel} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="page-btn"
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    ← Prev
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      className={`page-btn ${page === p ? 'active' : ''}`}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    className="page-btn"
                    disabled={page === totalPages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </main>
      </div>

      <Footer />
    </div>
  );
};

export default Hotels;
